///
/// EarthBrowser
///
/// Created by Matt Giger on 9/23/2026
/// Copyright (c) 2026 Geodata Labs
///
/// RTK Query endpoints for story tours sync (/api/user/stories) plus the
/// camelCase <-> wire (snake_case) conversions shared with story shares.
/// These are driven by the sync orchestrator (storiesSync.ts) and the
/// live-push store listener rather than component hooks: the local slice
/// is the source of truth and works fully offline/signed-out.
///

import type {
	PutStoryRequest,
	ShareStoryPayload,
	StoriesDelta,
	StoryDatasetRecord,
	StoryRecord,
	StoryStopRecord,
} from "@shared-types"
import { authenticatedApi } from "./API/authenticatedApi"
import type { Story, StoryDatasetRef, StoryStop } from "./storiesSlice"
import { validateStory } from "./validation/storiesValidation"

function stopToRecord(stop: StoryStop): StoryStopRecord {
	return {
		id: stop.id,
		camera: { ...stop.camera },
		title: stop.title,
		body: stop.body,
		focus_target_id: stop.focusTargetId ?? null,
		layers: stop.layers,
		dwell: { ...stop.dwell },
		annotation_ids: stop.annotationIds,
		dataset_ids: stop.datasetIds,
		source_urls: stop.sourceUrls,
	}
}

function datasetToRecord(ref: StoryDatasetRef): StoryDatasetRecord {
	return {
		id: ref.id,
		name: ref.name,
		filename: ref.filename,
		format: ref.format,
		cdn_url: ref.cdnUrl,
		feature_count: ref.featureCount ?? null,
		bounds: ref.bounds ?? null,
		opacity: ref.opacity ?? null,
	}
}

function recordToDataset(record: StoryDatasetRecord): StoryDatasetRef {
	const ref: StoryDatasetRef = {
		id: record.id,
		name: record.name,
		filename: record.filename,
		format: record.format,
		cdnUrl: record.cdn_url,
	}
	if (typeof record.feature_count === "number") ref.featureCount = record.feature_count
	if (record.bounds) ref.bounds = { ...record.bounds }
	if (typeof record.opacity === "number") ref.opacity = record.opacity
	return ref
}

function recordToStop(record: StoryStopRecord): StoryStop {
	const stop: StoryStop = {
		id: record.id,
		camera: { ...record.camera },
		title: record.title,
		body: record.body,
		dwell: { ...record.dwell },
	}
	if (record.focus_target_id) stop.focusTargetId = record.focus_target_id
	if (record.layers && record.layers.length > 0) stop.layers = [...record.layers]
	if (record.annotation_ids && record.annotation_ids.length > 0) stop.annotationIds = [...record.annotation_ids]
	if (record.dataset_ids && record.dataset_ids.length > 0) stop.datasetIds = [...record.dataset_ids]
	if (record.source_urls && record.source_urls.length > 0) stop.sourceUrls = [...record.source_urls]
	return stop
}

/** Convert a client story to the PUT request body (camelCase -> wire) */
export function storyToPutRequest(story: Story): PutStoryRequest {
	return {
		title: story.title,
		stops: story.stops.map(stopToRecord),
		annotations: story.annotations,
		datasets: story.datasets.length > 0 ? story.datasets.map(datasetToRecord) : undefined,
		tags: story.tags,
		created_at: story.createdAt,
		updated_at: story.updatedAt,
	}
}

/** Convert a server row to a client story (validated; null if malformed) */
export function recordToStory(record: StoryRecord): Story | null {
	return validateStory({
		id: record.id,
		title: record.title,
		stops: (record.stops ?? []).map(recordToStop),
		annotations: record.annotations ?? [],
		datasets: (record.datasets ?? []).map(recordToDataset),
		tags: record.tags ?? [],
		createdAt: record.created_at,
		updatedAt: record.updated_at,
	})
}

/** Self-contained snapshot for a story share link */
export function storyToSharePayload(story: Story): ShareStoryPayload {
	return {
		story: {
			id: story.id,
			title: story.title,
			stops: story.stops.map(stopToRecord),
			annotations: story.annotations,
			datasets: story.datasets.length > 0 ? story.datasets.map(datasetToRecord) : undefined,
		},
	}
}

/** Rebuild a playable client story from a resolved share snapshot */
export function storyFromSharePayload(payload: ShareStoryPayload, createdAt: string): Story | null {
	const story = payload?.story
	if (!story || typeof story !== "object") return null
	return validateStory({
		id: story.id,
		title: story.title,
		stops: (story.stops ?? []).map(recordToStop),
		annotations: story.annotations ?? [],
		datasets: (story.datasets ?? []).map(recordToDataset),
		tags: [],
		createdAt,
		updatedAt: createdAt,
	})
}

export const storiesApi = authenticatedApi.injectEndpoints({
	endpoints: (builder) => ({
		getStoriesDelta: builder.query<StoriesDelta, { since: string | null }>({
			query: ({ since }) => ({
				url: since
					? `/api/user/stories?since=${encodeURIComponent(since)}`
					: "/api/user/stories",
			}),
			providesTags: ["UserCollections"],
		}),
		putStory: builder.mutation<{ success: boolean }, { id: string; body: PutStoryRequest }>({
			query: ({ id, body }) => ({
				url: `/api/user/stories/${id}`,
				method: "PUT",
				body,
			}),
		}),
		deleteStoryRemote: builder.mutation<{ success: boolean }, string>({
			query: (id) => ({
				url: `/api/user/stories/${id}`,
				method: "DELETE",
			}),
		}),
	}),
})

export const {
	useGetStoriesDeltaQuery,
	usePutStoryMutation,
	useDeleteStoryRemoteMutation,
} = storiesApi
